/* eslint-disable max-lines-per-function */
import './Baseplate.scss'

import useGridUnit from '@websrc/App/Hooks/useGridUnit'
import React, {
  CSSProperties,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { Brick, BrickColor } from '../Brick/Brick'
import { Stud } from '../Stud/Stud'
import { calculateBrickPositions, calculateStudRangePositions } from './utility/baseplateUtils'

/**
 * Defines the possible snap positions for the stud range.
 */
type SnapPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

/**
 * Defines the interface for a brick's position and properties.
 */
export interface BrickPosition {
  /** The row index where the brick's specified corner should snap to. */
  row: number
  /** The column index where the brick's specified corner should snap to. */
  column: number
  /** The color of the brick, defined by the BrickColor enum. */
  color: BrickColor
  /** The snap position of the brick, indicating which corner of the brick should snap to the specified row and column. */
  snapPosition: SnapPosition
}

/**
 * Defines the interface for the stud range.
 */
export interface StudRange {
  /** The snap position for the stud range. */
  snapPosition: SnapPosition
  /** The number of rows/columns for the stud range, or -1 to cover the full height/width. */
  size: { rows: number | -1; columns: number | -1 }
  /** Optional offsets to grow the baseplate outside the element. */
  offset?: { top?: number; right?: number; bottom?: number; left?: number }
  /** An array of brick positions, each defining a brick to be displayed within the stud range. */
  bricks?: BrickPosition[]
}

/**
 * Defines the props for the Baseplate component.
 */
interface BaseplateProps {
  /** An array of stud ranges, each defining a rectangle of studs to be displayed. */
  studRanges: StudRange[]
  /** The children elements to be displayed on top of the studs. */
  children: ReactNode
  /** Optional media queries to determine when the baseplate should be rendered. */
  hideBaseplate?: boolean
  fixedUnit?: number
  style?: CSSProperties
  className?: string
}
export const Baseplate: FC<BaseplateProps> = ({
  studRanges,
  children,
  hideBaseplate = false,
  fixedUnit,
  ...props
}) => {
  const dynamicGridUnit = useGridUnit()
  const gridUnit = fixedUnit ? fixedUnit : dynamicGridUnit

  const [studPositions, setStudPositions] = useState<React.CSSProperties[]>([])
  const [bricks, setBricks] = useState<{ style: CSSProperties; color: BrickColor }[]>([])
  const baseplateRef = useRef<HTMLDivElement>(null)

  /**
   * Calculates the positions of the studs based on the stud ranges.
   */
  const calculateStudPositions = useCallback(() => {
    if (!baseplateRef.current) return []

    const baseplateRect = baseplateRef.current.getBoundingClientRect()
    const positions: React.CSSProperties[] = []

    // Iterate over each child element and its corresponding stud range
    Array.from(baseplateRef.current.children).forEach((child, index) => {
      const childElement = child as HTMLElement
      const rect = childElement.getBoundingClientRect()
      const range = studRanges[index]

      if (!range) {
        console.warn(`No stud range found for child element at index ${index}`)
        return
      }

      const { startRow, endRow, startColumn, endColumn, offsetColumn } =
        calculateStudRangePositions(rect, baseplateRect, range, gridUnit)

      // Calculate positions for each stud in the range
      for (let row = startRow; row <= endRow; row++) {
        for (let col = startColumn; col <= endColumn; col++) {
          const positionStyle: React.CSSProperties = {
            top: row * gridUnit,
          }

          if (range.snapPosition.includes('left')) {
            positionStyle.left = col * gridUnit
          } else {
            positionStyle.right = (endColumn - col) * gridUnit - offsetColumn * gridUnit
          }

          positions.push(positionStyle)
        }
      }
    })

    setStudPositions(positions)
  }, [studRanges, gridUnit])

  useEffect(() => {
    calculateStudPositions()
    const resizeObserver = new ResizeObserver(calculateStudPositions)
    const currentRef = baseplateRef.current
    if (currentRef) {
      resizeObserver.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        resizeObserver.unobserve(currentRef)
      }
    }
  }, [calculateStudPositions])

  // Calculate brick positions once on initial render
  useEffect(() => {
    if (!baseplateRef.current) return
    const baseplateRect = baseplateRef.current.getBoundingClientRect()
    const brickPositions = calculateBrickPositions(studRanges, baseplateRect, gridUnit)
    setBricks(brickPositions)
  }, [studRanges, gridUnit])

  if (hideBaseplate) {
    return <>{children}</>
  }

  return (
    <div className={`baseplate-container ${props.className}`} ref={baseplateRef} {...props}>
      <div className={'baseplate'}>
        {studPositions.map((style, index) => (
          <div className={'baseplate__item'} key={index} style={style}>
            <Stud className={'baseplate__stud'} />
          </div>
        ))}
      </div>
      <div className={'baseplate__content'}>{children}</div>
      {bricks.map((brickProp, index) => (
        <Brick
          brickColor={brickProp.color as BrickColor}
          className={'baseplate__brick'}
          key={index}
          style={brickProp.style}
        />
      ))}
    </div>
  )
}
